import { createContext, use } from "react";
import type { JSX, ReactNode } from "react";
import type { PlatformServices } from "../../platform/PlatformServices";
import { useI18n } from "../i18n/I18nContext";
import type { MessageKey } from "../i18n/messages.de";
import styles from "./LegalLinks.module.css";

/**
 * Paths of the app variants of the legal pages on the operator's website: without the main
 * navigation and without any link to prices, registration or checkout (ADR-0087 decision 2).
 */
export const appLegalPagePaths = {
  privacyPolicy: "/app/datenschutz/",
  imprint: "/app/impressum/",
  accessibility: "/app/barrierefreiheit/",
  security: "/app/sicherheit/",
} as const;

/** A link to a legal page of the operator. */
export interface LegalLink {
  /** Label of the link. */
  readonly labelKey: MessageKey;
  /** Absolute `https:` address of the page. */
  readonly href: string;
}

/**
 * Returns the legal pages shown in the app: privacy policy (Apple 5.1.1(i)), imprint
 * (`SAAS-IMPRINT`, § 5 DDG), accessibility statement and vulnerability reporting, as absolute
 * addresses of the app variants, because a site path does not resolve inside the native apps.
 * The browser shows none, so the PWA stays as it is; its pages belong to the website.
 *
 * @param platform - The platform services chosen in the composition root.
 * @param websiteUrl - Absolute `https:` address of the operator's website without trailing slash.
 * @returns The links in display order; empty in the browser.
 */
export function appLegalLinks(
  platform: Pick<PlatformServices, "isNative">,
  websiteUrl: string,
): readonly LegalLink[] {
  const pages: readonly { labelKey: MessageKey; path: string }[] = [
    { labelKey: "legalLinks.privacyPolicy", path: appLegalPagePaths.privacyPolicy },
    { labelKey: "legalLinks.imprint", path: appLegalPagePaths.imprint },
    { labelKey: "legalLinks.accessibility", path: appLegalPagePaths.accessibility },
    { labelKey: "legalLinks.security", path: appLegalPagePaths.security },
  ];
  const links = platform.isNative
    ? pages.map((page) => ({ labelKey: page.labelKey, href: `${websiteUrl}${page.path}` }))
    : [];

  return links;
}

const LegalLinksContext = createContext<readonly LegalLink[] | null>(null);

/** Properties of {@link LegalLinksProvider}. */
export interface LegalLinksProviderProps {
  /** The links chosen in the composition root. */
  readonly links: readonly LegalLink[];
  /** Child elements. */
  readonly children: ReactNode;
}

/**
 * Provides the links to the legal pages chosen in the composition root.
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function LegalLinksProvider(props: LegalLinksProviderProps): JSX.Element {
  const provider = <LegalLinksContext value={props.links}>{props.children}</LegalLinksContext>;

  return provider;
}

/**
 * Returns the links to the legal pages.
 *
 * @returns The links in display order.
 * @throws Error if no {@link LegalLinksProvider} is present.
 */
export function useLegalLinks(): readonly LegalLink[] {
  const links = use(LegalLinksContext);

  if (links === null) {
    throw new Error("useLegalLinks must be used inside a LegalLinksProvider.");
  }

  return links;
}

/**
 * Links to the operator's legal pages; they open outside the app. Shows nothing where no links
 * are provided (the browser).
 *
 * @returns The navigation element, or `null`.
 */
export function LegalLinkList(): JSX.Element | null {
  const { t } = useI18n();
  const links = useLegalLinks();

  if (links.length === 0) {
    return null;
  }

  const list = (
    <nav aria-label={t("legalLinks.title")}>
      <ul className={styles["list"]}>
        {links.map((link) => (
          <li key={link.href}>
            <a href={link.href} target="_blank" rel="noopener noreferrer">
              {t(link.labelKey)}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );

  return list;
}
